import {NumberFormatter, useFormatNumber} from 'platform/locale';
import {Nullish} from 'utility-types';

import {useTranslation} from 'react-i18next';

import {isNil} from 'ramda';

import {getSafely} from 'shared/web';

import {FiltersType} from '../../../types/FiltersType';
import {useFilters} from '../../FiltersContext/FiltersContext';

type NumberTupleKeys<T> = Extract<
  {
    [K in keyof T]: T[K] extends [number, number]
      ? K
      : T[K] extends [number, number] | undefined
        ? K
        : never;
  }[keyof T],
  keyof T
>;

export type NumberBasedFilterName = NumberTupleKeys<FiltersType>;

export const getNumberBasedTags = (
  filterName: NumberBasedFilterName,
  translation: string,
  fromTranslation: string,
  toTranslation: string,
  filters: FiltersType,
  onDelete: () => void,
  formatNumber: NumberFormatter,
  max?: number,
  unit: string = ''
) => {
  const filterValue = getSafely(filters, filterName);
  const formatter = filterName === 'year' ? (value: number) => value : formatNumber;

  if (isNil(filterValue)) {
    return [];
  }

  const from = ` ${fromTranslation} ${formatter(filterValue[0])}${unit}`;
  const to = filterValue[1] === max ? '' : ` ${toTranslation} ${formatter(filterValue[1])}${unit}`;

  return [
    {
      label: `${translation}${from}${to}`,
      onDelete,
    },
  ];
};

export const useGetNumberBasedTags = () => {
  const {filters, onUpdateFilters} = useFilters();
  const formatNumber = useFormatNumber();
  const {t} = useTranslation();

  return (
    filterName: NumberBasedFilterName,
    translation: string,
    max?: number,
    unit?: string,
    extra?: {
      formatter?: (number: number | Nullish) => string | null;
      onDelete?: () => void;
    }
  ) =>
    getNumberBasedTags(
      filterName,
      translation,
      t('general.labels.from'),
      t('general.labels.to'),
      filters,
      extra?.onDelete ?? (() => onUpdateFilters(filterName, undefined)),
      extra?.formatter ?? formatNumber,
      max,
      unit
    );
};
